import { useState } from 'react'
import Editor, { DiffEditor } from '@monaco-editor/react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useAgentVersions } from '@/hooks'
import { formatDateShort } from '@/lib/timeFormatters'
import { cn } from '@/lib/utils'
import type { AgentDetail, AgentVersion } from '@/types'

type ViewMode = 'view' | 'compare'

interface AgentVersionHistoryPanelProps {
  projectId: string
  agent: AgentDetail
  currentEditorYaml: string
}

export function AgentVersionHistoryPanel({ projectId, agent, currentEditorYaml }: AgentVersionHistoryPanelProps) {
  const { data: versions, isLoading, isError, refetch } = useAgentVersions(projectId, agent.id)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('view')

  const sorted = versions ?? []
  const isDraft = currentEditorYaml !== agent.yamlContent

  const selected =
    sorted.find(version => version.id === selectedId) ??
    sorted.find(version => version.id === agent.latestVersion?.id) ??
    sorted[0] ??
    null

  return (
    <div className="flex h-[560px] overflow-hidden rounded-2xl border border-border bg-card" data-testid="agent-version-history">
      <div className="flex w-80 shrink-0 flex-col gap-2 overflow-y-auto border-r p-3">
        <div className="px-1 pb-1">
          <p className="text-sm font-bold text-foreground">Version history</p>
          <p className="text-xs text-muted-foreground">
            Every save creates an immutable version. Workflows pin a version, so the resolved version is frozen onto each run for replay.
          </p>
        </div>
        {isLoading && (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">Loading...</div>
        )}
        {isError && (
          <div
            role="alert"
            data-testid="agent-version-history-error"
            className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center text-sm text-destructive"
          >
            <span>Failed to load version history.</span>
            <button className="underline" onClick={() => refetch()}>Retry</button>
          </div>
        )}
        {!isLoading && !isError && sorted.length === 0 && (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">No versions yet</div>
        )}
        {sorted.map(version => (
          <VersionListItem
            key={version.id}
            version={version}
            isCurrent={version.id === agent.latestVersion?.id}
            isSelected={version.id === selected?.id}
            onClick={() => setSelectedId(version.id)}
          />
        ))}
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center gap-1 border-b px-4 py-2">
          <Button
            variant={viewMode === 'view' ? 'default' : 'outline'}
            size="sm"
            aria-pressed={viewMode === 'view'}
            onClick={() => setViewMode('view')}
            className="h-7 px-3 text-xs"
          >
            View
          </Button>
          <Button
            variant={viewMode === 'compare' ? 'default' : 'outline'}
            size="sm"
            aria-pressed={viewMode === 'compare'}
            onClick={() => setViewMode('compare')}
            className="h-7 px-3 text-xs"
          >
            Compare with current
          </Button>
        </div>

        <div className="min-h-0 flex-1">
          {!selected ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Select a version to view
            </div>
          ) : viewMode === 'view' ? (
            <div className="h-full" data-testid="agent-version-view">
            <Editor
              height="100%"
              language="yaml"
              theme="vs-dark"
              value={selected.yamlSnapshot}
              options={{
                ariaLabel: `Agent configuration, version ${selected.version}, read-only`,
                readOnly: true,
                minimap: { enabled: false },
                fontSize: 14,
                lineNumbers: 'on',
                wordWrap: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 2,
                folding: true,
                bracketPairColorization: { enabled: true },
              }}
            />
            </div>
          ) : (
            <div className="flex h-full flex-col" data-testid="agent-version-diff">
            <div className="flex shrink-0 border-b text-xs font-semibold text-muted-foreground">
              <span className="flex-1 px-4 py-1.5">Version {selected.version}</span>
              <span className="flex-1 border-l px-4 py-1.5" data-testid="agent-version-diff-modified-heading">
                {isDraft ? 'Current draft (unpublished)' : 'Current'}
              </span>
            </div>
            <DiffEditor
              height="100%"
              language="yaml"
              theme="vs-dark"
              original={selected.yamlSnapshot}
              modified={currentEditorYaml}
              options={{
                originalAriaLabel: `Version ${selected.version}`,
                modifiedAriaLabel: isDraft ? 'Current draft, unpublished' : 'Current version',
                readOnly: true,
                renderSideBySide: true,
                minimap: { enabled: false },
                fontSize: 14,
                wordWrap: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
              }}
            />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface VersionListItemProps {
  version: AgentVersion
  isCurrent: boolean
  isSelected: boolean
  onClick: () => void
}

function VersionListItem({ version, isCurrent, isSelected, onClick }: VersionListItemProps) {
  return (
    <button
      type="button"
      data-testid={`agent-version-${version.version}`}
      onClick={onClick}
      aria-current={isSelected}
      className={cn(
        'w-full rounded-xl border px-4 py-3 text-left transition-colors',
        isSelected ? 'border-foreground bg-muted/40' : 'border-border hover:bg-muted/40',
      )}
    >
      <div className="mb-1.5 flex items-center gap-2">
        <span className="truncate font-mono text-sm font-bold text-foreground">@{version.version}</span>
        {isCurrent ? (
          <Badge
            variant="outline"
            className="shrink-0 border-success-foreground/40 bg-success px-2 py-0 text-[10px] font-medium text-success-foreground"
          >
            Current
          </Badge>
        ) : (
          <Badge variant="outline" className="shrink-0 px-2 py-0 text-[10px] font-medium text-muted-foreground">
            Retained for replay
          </Badge>
        )}
      </div>
      <p className="text-sm text-foreground">{`Version ${version.version}`}</p>
      <p className="mt-1 text-xs italic text-muted-foreground">
        {version.createdByName ? `${version.createdByName} · ` : ''}{formatDateShort(version.createdAt)}
      </p>
    </button>
  )
}
